/**
 * Date utility functions for PAM applications
 *
 * All functions handle null/undefined gracefully and work with
 * various input formats (Date, string, dayjs objects)
 */

/**
 * Format a date to YYYY-MM-DD string (UTC)
 *
 * @param {Date|string|Object} date - Date to format
 * @returns {string|null} Formatted date string or null
 *
 * @example
 * formatDate(new Date('2024-01-15')) // '2024-01-15'
 * formatDate('2024-01-15T12:00:00Z') // '2024-01-15'
 */
export const formatDate = (date) => {
  if (!date) return null;

  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return null;
  }
};

/**
 * Format a datetime for display in local timezone
 *
 * @param {Date|string|Object} datetime - Datetime to format
 * @param {Object} [options] - Intl.DateTimeFormat options
 * @returns {string|null} Formatted datetime string or null
 *
 * @example
 * formatDateTimeForDisplay('2024-01-15T12:30:00Z')
 * // '1/15/2024, 7:30:00 AM' (in EST)
 */
export const formatDateTimeForDisplay = (datetime, options = {}) => {
  if (!datetime) return null;

  try {
    const d = new Date(datetime);
    if (isNaN(d.getTime())) return null;

    const defaultOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      ...options,
    };

    return d.toLocaleString(undefined, defaultOptions);
  } catch {
    return null;
  }
};

/**
 * Format a datetime for saving to database (ISO UTC format)
 *
 * @param {Date|string|Object} datetime - Datetime to format
 * @returns {string|null} ISO formatted string or null
 *
 * @example
 * formatDateTimeForSave(new Date()) // '2024-01-15T12:30:00.000Z'
 */
export const formatDateTimeForSave = (datetime) => {
  if (!datetime) return null;

  try {
    const d = new Date(datetime);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch {
    return null;
  }
};

/**
 * Get the current UTC timestamp in ISO format
 *
 * @returns {string} Current UTC timestamp
 *
 * @example
 * getCurrentUtcTimestamp() // '2024-01-15T12:30:00.000Z'
 */
export const getCurrentUtcTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Parse a date string or object to a Date object
 *
 * @param {Date|string|Object|null} dateInput - Date input to parse
 * @returns {Date|null} Parsed Date object or null
 *
 * @example
 * parseDate('2024-01-15') // Date object
 * parseDate(null) // null
 */
export const parseDate = (dateInput) => {
  if (!dateInput) return null;

  try {
    // Already a Date
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // dayjs object
    if (dateInput.$d instanceof Date) {
      return dateInput.$d;
    }

    // String or other
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

/**
 * Check if a date is valid
 *
 * @param {any} date - Value to check
 * @returns {boolean} Whether the date is valid
 */
export const isValidDate = (date) => {
  return parseDate(date) !== null;
};

/**
 * Get the start of day (midnight UTC) for a given date
 *
 * @param {Date|string} date - Input date
 * @returns {Date|null} Start of day or null
 */
export const startOfDay = (date) => {
  const d = parseDate(date);
  if (!d) return null;

  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

/**
 * Get the end of day (23:59:59.999 UTC) for a given date
 *
 * @param {Date|string} date - Input date
 * @returns {Date|null} End of day or null
 */
export const endOfDay = (date) => {
  const d = parseDate(date);
  if (!d) return null;

  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
};

/**
 * Add days to a date
 *
 * @param {Date|string} date - Input date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date|null} New date or null
 */
export const addDays = (date, days) => {
  const d = parseDate(date);
  if (!d) return null;

  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Get the difference between two dates in days
 *
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {number|null} Difference in days or null
 */
export const daysBetween = (date1, date2) => {
  const d1 = parseDate(date1);
  const d2 = parseDate(date2);

  if (!d1 || !d2) return null;

  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
